/* eslint-disable max-lines-per-function */
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Drawer, DrawerContent, DrawerTrigger } from '@/components/ui/drawer';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { cn } from '@/lib/utils';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { HTMLAttributes, useState } from 'react';
import { useTranslation } from 'react-i18next';

export function DatePicker({
  defaultDate,
  onSelect,
  drawerLabel,
  disabled = false,
}: {
  defaultDate?: Date | undefined;
  onSelect: (date: Date) => void;
  drawerLabel: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    defaultDate,
  );
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  function handleSelect(day: Date) {
    setSelectedDate(day);
    onSelect(day);
  }

  return (
    <div>
      <Popover onOpenChange={setIsPopoverOpen} open={isPopoverOpen}>
        <PopoverTrigger className="hidden md:flex" asChild>
          <Trigger selectedDate={selectedDate} disabled={disabled} />
        </PopoverTrigger>
        <PopoverContent
          className=" bg-white w-fit flex flex-col justify-center"
          align="start">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={(day) => {
              if (!day) {
                return;
              }
              handleSelect(day);
              setIsPopoverOpen(false);
            }}
            defaultMonth={selectedDate}
            initialFocus
            classNames={{ day_today: 'bg-transparent' }}
          />
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleSelect(new Date());
              setIsPopoverOpen(false);
            }}
            className="w-full"
            variant="ghost">
            {t('general.date.today')}
          </Button>
        </PopoverContent>
      </Popover>
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <Trigger selectedDate={selectedDate} disabled={disabled} />
        </DrawerTrigger>
        <DrawerContent className="flex flex-col gap-4 p-4">
          <div className="font-semibold text-center">{drawerLabel}</div>
          <Calendar
            className="w-full"
            mode="single"
            selected={selectedDate}
            onSelect={(day) => {
              if (!day) {
                return;
              }
              handleSelect(day);
              setIsDrawerOpen(false);
            }}
            defaultMonth={selectedDate}
            initialFocus
            classNames={{
              day_today: 'bg-transparent',
              cell: 'w-full',
              head_cell:
                'text-slate-500 rounded-md w-9 font-normal text-[0.8rem] dark:text-slate-400 w-full',
            }}
          />
          <Button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              handleSelect(new Date());
              setIsDrawerOpen(false);
            }}
            className="w-full"
            variant="ghost">
            {t('general.date.today')}
          </Button>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

function Trigger({
  selectedDate,
  disabled,
  ...props
}: {
  selectedDate?: Date | undefined;
  disabled: boolean;
} & HTMLAttributes<HTMLButtonElement>) {
  const { t } = useTranslation();
  return (
    <Button
      {...props}
      disabled={disabled}
      variant="outline-secondary"
      role="combobox"
      className={cn(
        'w-full justify-between font-normal  [&>svg]:text-neutrals-700',
        selectedDate ? 'text-neutrals-900' : 'text-neutrals-700',
        props.className,
      )}>
      {selectedDate
        ? dayjs(selectedDate).format('DD.MM.YYYY')
        : t('actions.auswaehlen')}
      <FontAwesomeIcon icon={faCalendar} />
    </Button>
  );
}
